import api from './apiClient';
import axios from 'axios';

type ChannelRouteTarget = number | string | { id?: number | string; hashId?: string | null; channelHashId?: string | null };

const resolveChannelRouteId = (channel: ChannelRouteTarget) => {
  if (channel && typeof channel === 'object') {
    return String(channel.channelHashId || channel.hashId || channel.id || '');
  }
  return String(channel ?? '');
};

export interface MediaBoxProfileOption {
  id: string;
  label: string;
  description: string;
  enabled: boolean;
}

export interface MediaBoxConfig {
  playbackEnabled: boolean;
  playAllAssets: boolean;
  loopPlayback: boolean;
  backupEnabled: boolean;
  backupThresholdSecs: number;
  uploadProfile: MediaBoxUploadProfile;
  selectedAssetIds: number[];
  runtime?: {
    running: boolean;
    mode: string;
    armed: boolean;
    startedAt?: string | null;
    activeAssetCount: number;
    lastError?: string;
    currentAssetId?: number | null;
    nextAssetId?: number | null;
  } | null;
}

export interface MediaBoxAsset {
  id: number;
  uuid: string;
  label: string;
  originalPath: string;
  normalisedPath?: string | null;
  durationSecs?: number | null;
  width?: number | null;
  height?: number | null;
  fps?: string | null;
  videoCodec?: string | null;
  audioCodec?: string | null;
  sampleRate?: number | null;
  channels?: number | null;
  bitrate?: number | null;
  fileSizeBytes?: number | null;
  normStatus: string;
  normStage?: string | null;
  normProgressPct?: number | null;
  normError?: string | null;
  isCurrent?: boolean;
  isNext?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MediaBoxUploadState {
  active: boolean;
  fileName: string;
  progress: number;
}

export type MediaBoxUploadProfile = 'h264_opus' | 'h264_aac';

const uploadStates = new Map<string, MediaBoxUploadState>();
const uploadControllers = new Map<string, AbortController>();
const uploadListeners = new Set<() => void>();

const emitUploadState = () => {
  uploadListeners.forEach((listener) => listener());
};

export const subscribeMediaBoxUploads = (listener: () => void) => {
  uploadListeners.add(listener);
  return () => uploadListeners.delete(listener);
};

export const getMediaBoxUploadState = (channel: ChannelRouteTarget): MediaBoxUploadState | null => {
  const routeId = resolveChannelRouteId(channel);
  return uploadStates.get(routeId) || null;
};

export const cancelMediaBoxUpload = (channel: ChannelRouteTarget) => {
  const routeId = resolveChannelRouteId(channel);
  const controller = uploadControllers.get(routeId);
  if (controller) {
    controller.abort();
  }
};

export interface MediaBoxSummary {
  channelId: number;
  addonId: number;
  tierCode: string;
  storageBytes: number;
  usedBytes: number;
  remainingBytes: number;
  assetCount: number;
  config: MediaBoxConfig;
  profiles: MediaBoxProfileOption[];
  houseProfile: MediaBoxProfileOption;
}

export interface MediaBoxRuntimeSnapshot {
  runtime?: MediaBoxConfig['runtime'] | null;
  currentAssetId?: number | null;
  currentAssetLabel?: string | null;
  currentAssetAudioCodec?: string | null;
  nextAssetId?: number | null;
  nextAssetLabel?: string | null;
  nextAssetAudioCodec?: string | null;
}

export const getMediaBoxSummary = async (channel: ChannelRouteTarget): Promise<MediaBoxSummary> => {
  const routeId = resolveChannelRouteId(channel);
  const { data } = await api.get<MediaBoxSummary>(`/mediabox/channels/${routeId}/summary`);
  return data;
};

export const getMediaBoxRuntime = async (channel: ChannelRouteTarget): Promise<MediaBoxRuntimeSnapshot> => {
  const routeId = resolveChannelRouteId(channel);
  const { data } = await api.get<MediaBoxRuntimeSnapshot>(`/mediabox/channels/${routeId}/runtime`);
  return data;
};

export const getMediaBoxAssets = async (channel: ChannelRouteTarget, options?: { optional?: boolean }): Promise<MediaBoxAsset[]> => {
  const routeId = resolveChannelRouteId(channel);
  const { data } = await api.get<{ items: MediaBoxAsset[] }>(`/mediabox/channels/${routeId}/assets`, {
    params: options?.optional ? { optional: '1' } : undefined,
  });
  return Array.isArray(data?.items) ? data.items : [];
};

export const uploadMediaBoxAsset = async (
  channel: ChannelRouteTarget,
  file: File,
  label?: string,
  onUploadProgress?: (percent: number) => void,
): Promise<MediaBoxAsset> => {
  const routeId = resolveChannelRouteId(channel);
  const formData = new FormData();
  const controller = new AbortController();
  uploadControllers.set(routeId, controller);
  uploadStates.set(routeId, {
    active: true,
    fileName: label?.trim() || file.name,
    progress: 0,
  });
  emitUploadState();
  formData.append('file', file);
  if (label?.trim()) formData.append('label', label.trim());
  try {
    const { data: grant } = await api.post<{ url: string; maxBytes: number }>(`/uploads/channels/${routeId}/grants`, {
      target: 'mediabox',
      fileName: label?.trim() || file.name,
      fileSize: file.size,
    });
    if (file.size > Number(grant.maxBytes || 0)) {
      throw new Error('This file exceeds the available MediaBox storage capacity.');
    }
    const { data } = await axios.post<MediaBoxAsset>(grant.url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      withCredentials: true,
      signal: controller.signal,
      onUploadProgress: (event) => {
        if (!event.total) return;
        const percent = Math.max(0, Math.min(100, Math.round((event.loaded / event.total) * 100)));
        uploadStates.set(routeId, {
          active: true,
          fileName: label?.trim() || file.name,
          progress: percent,
        });
        emitUploadState();
        onUploadProgress?.(percent);
      },
    });
    return data;
  } finally {
    uploadControllers.delete(routeId);
    uploadStates.delete(routeId);
    emitUploadState();
  }
};

export const renameMediaBoxAsset = async (channel: ChannelRouteTarget, assetId: number, label: string) => {
  const routeId = resolveChannelRouteId(channel);
  const { data } = await api.patch(`/mediabox/channels/${routeId}/assets/${assetId}`, { label });
  return data;
};

export const deleteMediaBoxAsset = async (channel: ChannelRouteTarget, assetId: number) => {
  const routeId = resolveChannelRouteId(channel);
  await api.delete(`/mediabox/channels/${routeId}/assets/${assetId}`);
};

export const reprocessMediaBoxAsset = async (channel: ChannelRouteTarget, assetId: number) => {
  const routeId = resolveChannelRouteId(channel);
  await api.post(`/mediabox/channels/${routeId}/assets/${assetId}/reprocess`);
};

export const cancelMediaBoxProcessing = async (channel: ChannelRouteTarget, assetId: number) => {
  const routeId = resolveChannelRouteId(channel);
  await api.post(`/mediabox/channels/${routeId}/assets/${assetId}/cancel`);
};

export const getMediaBoxPlayback = async (channel: ChannelRouteTarget): Promise<MediaBoxConfig> => {
  const routeId = resolveChannelRouteId(channel);
  const { data } = await api.get<MediaBoxConfig>(`/mediabox/channels/${routeId}/playback`);
  return data;
};

export const updateMediaBoxPlayback = async (channel: ChannelRouteTarget, payload: MediaBoxConfig): Promise<MediaBoxConfig> => {
  const routeId = resolveChannelRouteId(channel);
  const { data } = await api.put<MediaBoxConfig>(`/mediabox/channels/${routeId}/playback`, payload);
  return data;
};

export const getMediaBoxThumbnailUrl = (channel: ChannelRouteTarget, assetId: number) => {
  const routeId = resolveChannelRouteId(channel);
  return `${api.defaults.baseURL}/mediabox/channels/${routeId}/assets/${assetId}/thumbnail`;
};

export const fetchMediaBoxThumbnail = async (channel: ChannelRouteTarget, assetId: number): Promise<Blob> => {
  const routeId = resolveChannelRouteId(channel);
  const { data } = await api.get(`/mediabox/channels/${routeId}/assets/${assetId}/thumbnail`, {
    responseType: 'blob',
  });
  return data as Blob;
};
